/**
 * DisplayNameModal Component
 *
 * Shared modal for display name editing functionality.
 * Used by both regular users and admin users.
 * Extracted from UserAccountManagement for reusability.
 */

// 1. External dependencies
import React, { useState, useCallback, useEffect } from 'react';

// 2. Internal absolute imports
import {
  Button,
  Input,
  Form,
  Alert,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter
} from '@enfomentis/ui';
import { useAuth } from '@/hooks/useAuth';
import { useToastNotification } from '@enfomentis/ui';

/**
 * Props for DisplayNameModal
 */
export interface DisplayNameModalProps {
  isOpen: boolean;
  onOpenChange: () => void;
  onSuccess?: () => void;
  currentDisplayName?: string;
}

/**
 * Display Name Modal Component
 *
 * Features:
 * - Edit user display name
 * - Character length validation (2-50 chars)
 * - Character type validation (letters, numbers, spaces, hyphens, apostrophes)
 * - Real-time validation
 * - Success toast notification
 */
export const DisplayNameModal: React.FC<DisplayNameModalProps> = ({
  isOpen,
  onOpenChange,
  onSuccess,
  currentDisplayName
}) => {
  const { user, updateDisplayName } = useAuth();
  const toast = useToastNotification();

  // Form state
  const [displayName, setDisplayName] = useState(currentDisplayName || user?.displayName || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Update display name field when user data or currentDisplayName changes
  useEffect(() => {
    setDisplayName(currentDisplayName || user?.displayName || '');
  }, [currentDisplayName, user?.displayName]);

  // Display name validation function
  const validateDisplayName = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) return "Display name is required";
    if (trimmed.length < 2) return "Display name must be at least 2 characters long";
    if (trimmed.length > 50) return "Display name must not exceed 50 characters";
    // Allow letters, numbers, spaces, hyphens, apostrophes
    if (!/^[a-zA-Z0-9\s\-']+$/.test(trimmed)) return "Display name can only contain letters, numbers, spaces, hyphens, and apostrophes";
    return null;
  };

  // Display name validation state
  const displayNameError = hasSubmitted ? validateDisplayName(displayName) : null;

  // Reset display name form state
  const resetForm = useCallback(() => {
    setDisplayName(currentDisplayName || user?.displayName || '');
    setServerErrors({});
    setHasSubmitted(false);
    setIsSubmitting(false);
  }, [currentDisplayName, user?.displayName]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  // Handle display name modal close
  const handleModalClose = useCallback(() => {
    resetForm();
    onOpenChange();
  }, [resetForm, onOpenChange]);

  // Handle display name form submission
  const handleDisplayNameUpdate = async (e?: React.FormEvent): Promise<void> => {
    if (e) {
      e.preventDefault();
    }

    setHasSubmitted(true);

    // Client-side validation before submission
    const validationError = validateDisplayName(displayName);
    if (validationError) {
      return; // Don't submit if there are validation errors
    }

    // Don't submit if no change
    if (displayName.trim() === user?.displayName) {
      setServerErrors({ 'general': 'No changes to save' });
      return;
    }

    setIsSubmitting(true);
    setServerErrors({});

    try {
      const result = await updateDisplayName(displayName.trim());

      if (result.success) {
        // Close modal immediately
        onOpenChange();
        // Show success toast
        toast.showToast({
          title: 'Display name updated',
          description: 'Your display name has been updated successfully',
          color: 'success',
          timeout: 4000
        });
        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setServerErrors({ 'general': result.message || 'Failed to update display name. Please try again' });
      }
    } catch {
      setServerErrors({ 'general': 'Network error occurred. Please check your connection and try again' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="md"
      placement="center"
      backdrop="blur"
      scrollBehavior="inside"
      isDismissable={!isSubmitting}
      isKeyboardDismissDisabled={isSubmitting}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h3 className="text-xl font-semibold">Edit Display Name</h3>
              <p className="text-sm text-text-secondary font-normal">
                This name is used to address you in communications and throughout the application
              </p>
            </ModalHeader>

            <ModalBody>
              <Form
                validationBehavior="aria"
                validationErrors={serverErrors}
                className="space-y-6"
                id="display-name-form"
              >
                {/* Display Name Input */}
                <Input
                  type="text"
                  label="Display Name"
                  placeholder="Enter your display name"
                  value={displayName}
                  onValueChange={setDisplayName}
                  validate={validateDisplayName}
                  isRequired
                  autoComplete="name"
                  errorMessage={serverErrors['displayName'] || displayNameError}
                  isInvalid={!!serverErrors['displayName'] || !!displayNameError}
                  classNames={{
                    input: "text-base", // 16px font size to prevent mobile zoom
                  }}
                />

                {/* General Error Display */}
                {serverErrors['general'] && (
                  <Alert
                    color="warning"
                    variant="flat"
                    description={serverErrors['general']}
                  />
                )}

                {/* Display Name Usage Info */}
                <div className="p-4 bg-default-50 rounded-lg">
                  <h5 className="text-sm font-medium text-text-primary mb-2">
                    Display Name Usage:
                  </h5>
                  <ul className="text-sm text-text-secondary space-y-1">
                    <li>• Used to address you in emails and communications</li>
                    <li>• Shown in your profile and account settings</li>
                    <li>• Can contain letters, numbers, spaces, hyphens, and apostrophes</li>
                    <li>• Must be between 2-50 characters</li>
                  </ul>
                </div>
              </Form>
            </ModalBody>

            <ModalFooter>
              <Button
                color="default"
                variant="light"
                onPress={handleModalClose}
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                isLoading={isSubmitting}
                onPress={() => handleDisplayNameUpdate()}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
